import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COOKIE_SETTINGS_EVENT, getConsent, setConsent } from "@/lib/consent";

// Consent banner for audience measurement. Shown until the visitor chooses, and again from
// "Manage cookies" links. Accept and refuse carry the same weight (CNIL guidance).
export default function CookieBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => getConsent() === null);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, show);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, show);
  }, []);

  if (!open) return null;

  const choose = (value) => {
    setConsent(value);
    setOpen(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t("cookies.title")}
      className="fixed inset-x-3 bottom-3 z-[90] mx-auto max-w-2xl rounded-sm border border-stone-200 bg-white p-4 shadow-xl sm:inset-x-6 sm:p-5"
      style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="text-sm font-semibold text-neutral-950">{t("cookies.title")}</div>
      <p className="mt-1 text-xs leading-relaxed text-neutral-600">
        {t("cookies.text")}{" "}
        <Link to="/legal/confidentialite" className="underline underline-offset-2 hover:text-neutral-950">
          {t("cookies.learnMore")}
        </Link>
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:justify-end">
        <button
          type="button"
          data-testid="cookie-refuse-btn"
          onClick={() => choose("denied")}
          className="h-10 rounded-sm border border-neutral-950 px-5 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-950 hover:bg-neutral-100"
        >
          {t("cookies.refuse")}
        </button>
        <button
          type="button"
          data-testid="cookie-accept-btn"
          onClick={() => choose("granted")}
          className="h-10 rounded-sm border border-neutral-950 bg-neutral-950 px-5 text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-neutral-800"
        >
          {t("cookies.accept")}
        </button>
      </div>
    </div>
  );
}
